<template>
  <UIInputSelect v-model="criteriaComparison" :size :data :disabled />
  <div v-if="isFieldVisible" class="module-rules-criteria-amount">
    <component
      :is="inputComponent"
      v-model="criteriaValue"
      :size
      :disabled
      data-refid="amountRuleCriteriaInput"
      @ready="handleReady"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue'

import {
  FilterComparison,
  FilterField,
  InputSize,
  TransactionRuleCriteria,
} from '@types'

import { getComparisonData } from '@/components/UI/Filter/components/utils/helpers'

import { UIInputNumber, UIInputPercent, UIInputSelect } from '@ui/fields'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria['params'][number]>({
  required: true,
})

const size = inject<InputSize>('size')

const isPercent = ref(false)

const criteriaComparison = computed({
  get() {
    return modelValue.value.comparison
  },
  set(comparison) {
    modelValue.value = {
      ...modelValue.value,
      comparison,
    }
  },
})

const criteriaValue = computed({
  get() {
    return modelValue.value.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const data = computed(() =>
  getComparisonData({ isAmount: true } as FilterField).map(value => ({
    value,
    label: value,
  })),
)

const inputComponent = computed(() =>
  isPercent.value ? UIInputPercent : UIInputNumber,
)

const isFieldVisible = computed(
  () =>
    criteriaComparison.value &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      criteriaComparison.value,
    ),
)

const handleReady = (el: HTMLInputElement) => {
  el?.focus()
}

watch(isPercent, () => {
  criteriaValue.value = null
})

watch(isFieldVisible, value => {
  criteriaValue.value = value ? null : undefined
})

onBeforeMount(() => {
  if (criteriaComparison.value) return
  criteriaComparison.value = FilterComparison.GREATER
})
</script>

<style>
.module-rules-criteria-amount {
  @apply flex items-center;
  @apply gap-4;

  .ui-input-text {
    @apply flex-auto;
  }
}
</style>
